import { Form } from "@/components/elements/Form";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useToast } from "@/hooks/useToast";
import { useTriggerValidate } from "@/hooks/useTriggerValidate";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { forwardRef, useImperativeHandle, useState } from "react";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { Button } from "@/components/elements/Button";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { getDynamicRoute } from "@/utils/url";
import { Typography } from "@mui/material";
import { useWatch } from "react-hook-form";
import SaveIcon from "@mui/icons-material/Save";
import { TCompanyForm } from "@/features/company/types/companyForm";
import { companySchema } from "@/features/company/schema/companySchema";
import { CompanyBasicInfoForm } from "@/features/company/components/form/CompanyBasicInfoForm";
import { CompanyContractForm } from "@/features/company/components/form/CompanyContractForm";
import { CompanyHandledIndustryForm } from "@/features/company/components/form/CompanyHandledIndustryForm";
import { CompanyOtherInfoForm } from "@/features/company/components/form/CompanyOtherInfoForm";
import { convertCompanyFormReq, convertCompanyFormToUpdateReq } from "@/features/company/utils/converts";
import { createCompanyApi, updateCompanyApi } from "@/api/services/admin/company";
import { ECompanyContractStatus } from "@/types/enum";
import { truncateWithEllipsis } from "@/utils/string";
import { Modal } from "@/components/elements/Modal";

type TCompanyFormProps = {
  previousData?: Partial<TCompanyForm>;
};

export const CompanyForm = forwardRef<{ submit: () => void }, TCompanyFormProps>(({ previousData }, ref) => {
  const { id } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const methods = useForm<TCompanyForm>({
    defaultValues: previousData,
    resolver: yupResolver(companySchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const [loading, setLoading] = useState(false);
  const [removedLockedIndustryNames, setRemovedLockedIndustryNames] = useState<string[]>([]);
  const [pendingSubmitData, setPendingSubmitData] = useState<TCompanyForm | null>(null);
  useTriggerValidate({ methods, name: "contractStatus", triggerFields: ["trialEndDate", "contractStartDate"] });
  useTriggerValidate({ methods, name: "contractStartDate", triggerFields: ["contractStatus", "trialEndDate", "contractEndDate"] });
  useTriggerValidate({ methods, name: "contractEndDate", triggerFields: ["contractStatus"] });
  useTriggerValidate({ methods, name: "trialStartDate", triggerFields: ["contractStatus", "trialEndDate", "contractStartDate"] });
  useTriggerValidate({ methods, name: "trialEndDate", triggerFields: ["contractStatus", "trialStartDate", "contractStartDate"] });

  // Detect originals with active learners that no longer appear in any industry slot.
  const getRemovedLockedIndustryNames = (): string[] => {
    const values = methods.getValues();
    const currentIds = [values.companySpecificIndustryId1, values.companySpecificIndustryId2, values.companySpecificIndustryId3]
      .filter((id) => id != null && (id as unknown) !== "")
      .map((id) => Number(id));
    const originals = [values.companySpecificIndustry1, values.companySpecificIndustry2, values.companySpecificIndustry3];
    return originals.filter((ind) => ind?.hasLearner && ind?.id != null && !currentIds.includes(Number(ind.id))).map((ind) => ind!.name);
  };

  const performSubmit = async (data: TCompanyForm) => {
    setLoading(true);
    try {
      if (id) {
        const payload = convertCompanyFormToUpdateReq(data);
        const rs = await updateCompanyApi(id, payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        navigate(getDynamicRoute(AppRoutes.companyDetail, { id }));
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL, id] });
      } else {
        const payload = convertCompanyFormReq(data);
        const rs = await createCompanyApi(payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        if (payload.contractStatus === ECompanyContractStatus.DURING_CONTRACT) {
          navigate(AppRoutes.companyDuringContractList);
        }
        if (payload.contractStatus === ECompanyContractStatus.CANCELED_CONTRACT) {
          navigate(AppRoutes.companyCanceledContractList);
        }
        if (payload.contractStatus === ECompanyContractStatus.TRIAL_CANCELLED) {
          navigate(AppRoutes.companyTrialCancelledList);
        }
        if (payload.contractStatus === ECompanyContractStatus.TRIAL) {
          navigate(AppRoutes.companyTrialList);
        }
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST] });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
    setLoading(false);
  };

  const onSubmit = async (data: TCompanyForm) => {
    const removedNames = getRemovedLockedIndustryNames();
    if (removedNames.length > 0) {
      setPendingSubmitData(data);
      setRemovedLockedIndustryNames(removedNames);
      return;
    }
    await performSubmit(data);
  };

  const handleWarningConfirm = async () => {
    const data = pendingSubmitData;
    setRemovedLockedIndustryNames([]);
    setPendingSubmitData(null);
    if (data) {
      await performSubmit(data);
    }
  };

  const handleWarningCancel = () => {
    setRemovedLockedIndustryNames([]);
    setPendingSubmitData(null);
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      await methods.handleSubmit(onSubmit)();
    },
  }));

  const { id: companyId } = useParams();

  const backRoute = companyId ? getDynamicRoute(AppRoutes.companyDetail, { id: companyId }) : AppRoutes.companyDuringContractList;

  const companyName = useWatch({ control: methods.control, name: "companyName" });
  const displayName = companyName || "(会社・法人名未入力)";

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={companySchema} initialValues={previousData}>
      <MenuNavigate
        menuPrefix={
          <Typography variant="body14" sx={{ color: "#0B0C0D", px: 2, py: 1, fontWeight: 700 }}>
            {truncateWithEllipsis(displayName, 30)}
          </Typography>
        }
        contents={[
          { title: "基本情報", content: <CompanyBasicInfoForm companyId={id} /> },
          { title: "契約情報", content: <CompanyContractForm /> },
          { title: "取り扱う分野", content: <CompanyHandledIndustryForm /> },
          { title: "その他（顧客未開示の情報）", content: <CompanyOtherInfoForm /> },
        ]}
        contentWidth="900px"
        contentAffix={
          <FooterContainer sx={{ mt: 4 }}>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button size="sm" variant="secondary" onClick={() => navigate(backRoute)}>
                戻る
              </Button>
              <Stack direction="row" gap={3}>
                <Button disabled={loading} size="sm" type="submit" startIcon={<SaveIcon />}>
                  保存
                </Button>
              </Stack>
            </Stack>
          </FooterContainer>
        }
      />
      <Modal
        isOpen={removedLockedIndustryNames.length > 0}
        onClose={handleWarningCancel}
        onOk={handleWarningConfirm}
        labelClose="いいえ"
        labelOk="はい"
        width={500}
        preventCloseBackdrop
        isLoadingOkBtn={loading}
        disabledOk={loading}
      >
        <Stack mb={2}>
          <Typography>
            {`すでに${removedLockedIndustryNames.map((name) => `「${name}」`).join("")}分野の外国人が登録されています。この分野を外すと、受験手続および学習進捗にて管理ができなくなりますが、本当によろしいですか？`}
          </Typography>
        </Stack>
      </Modal>
    </Form>
  );
});
